// Tone Architect → VOICE KIT — Sep 29, 2026 (Randy pt-voice-kit).
//
// The old screen took a 30,000-char transcript and asked an LLM to DESCRIBE
// his style, then committed that description to Tier 1 as an ordinary document.
// It made him sound like a caricature: "it made me say umm, like, basically —
// it was terrible." Root cause is in /app/memory/TONE_ARCHITECT_EVALUATION.md.
//
// This screen shows instead of tells. Five slots hold short SPECIMENS of his
// real writing — auto-pulled from documents he marked green ("100% mine") in
// the Library, or from replies he flagged as his own words in YTRE / the
// YouTube Replies inbox, or typed by hand. The tics the cleaner strips out of
// a swatch become the ban list. Nothing is stored in Tier 1.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Waveform, ArrowsClockwise, ArrowUUpLeft, PushPin, PushPinSlash, Eye,
  FileText, ChatCircleDots, PencilSimple, Scales, Prohibit, X,
  CaretRight, Trash, Plus, Ruler,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

const MODES = [
  { v: "legacy", l: "Legacy", d: "Today's behaviour — your Tier 1 documents are the voice filter." },
  { v: "kit", l: "Voice Kit", d: "The samples below own the voice. Tier 1 becomes facts only." },
  { v: "none", l: "None", d: "No voice steering at all — plain, direct prose." },
];
const SOURCES = [
  { v: "green", l: "Green docs", icon: FileText },
  { v: "reply", l: "Flagged replies", icon: ChatCircleDots },
  { v: "manual", l: "Typed by me", icon: PencilSimple },
];
const MODE_LABEL = {
  kit: "the Voice Kit",
  legacy: "Legacy",
  none: "None",
  unknown: "a setting from before this was tracked",
};

// Faults worth one automatic retry: the model was busy, not wrong.
const TRANSIENT_LLM = /(\b50[023]\b|\b429\b|overload|high demand|unavailable|temporar|try again)/i;

// Plain English for an upstream model failure, so an empty panel never reads
// as a defect in the writing samples. The common one is Gemini answering
// 503 "This model is currently experiencing high demand".
const probeFailureLine = (error) => {
  if (!error) {
    return "This side came back with no text. Your writing samples are fine. Tap Compare to ask again.";
  }
  if (/(\b503\b|overload|high demand|unavailable)/i.test(error)) {
    return "The AI model was busy and returned nothing on this side. Its own words were \"this model is currently experiencing high demand\", which is a temporary fault at Google and has nothing to do with your writing samples or your voice setting. Tap Compare to ask again.";
  }
  if (/(\b429\b|quota|rate.?limit)/i.test(error)) {
    return "The AI model refused this side on rate limits or quota. Nothing to do with your writing samples. Wait a moment, then tap Compare again.";
  }
  return `This side came back empty. The model reported: ${String(error).slice(0, 180)}`;
};

const ProbeSide = ({ label, labelClass, text, error, testid }) => (
  <div className="border hairline rounded-md p-3 bg-[#FDFCF8]" data-testid={testid}>
    <div className={`text-[10px] uppercase tracking-[0.18em] mb-1 ${labelClass}`}>{label}</div>
    {text ? (
      <p className="text-[12.5px] leading-relaxed text-[#1C1B1A] whitespace-pre-wrap">{text}</p>
    ) : (
      <p className="text-[11.5px] leading-relaxed text-[#8A3A1C]" data-testid={`${testid}-error`}>
        {probeFailureLine(error)}
      </p>
    )}
  </div>
);

export default function ToneArchitect({ siloId, siloName, onSwitchTab }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState("");
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState({});          // slotId -> local text edits
  const [probeQ, setProbeQ] = useState("");
  const [probe, setProbe] = useState(null);
  const [showRaw, setShowRaw] = useState("");
  const [showFp, setShowFp] = useState(false);
  const [showAntiAi, setShowAntiAi] = useState(false);
  const [extraSlots, setExtraSlots] = useState(0);   // slots revealed beyond the default three
  const [drift, setDrift] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/silos/${siloId}/voice-kit`);
      setState(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the Voice Kit.");
    }
  }, [siloId]);

  useEffect(() => { load(); }, [load]);

  const patch = async (body, quiet) => {
    setBusy("save");
    try {
      const r = await api.patch(`/silos/${siloId}/voice-kit`, body);
      setState((s) => ({ ...s, kit: r.data.kit }));
      load();  // re-measure: the fingerprint and overlap report move with the slots
      if (!quiet) toast.success("Saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally { setBusy(""); }
  };

  const reroll = async (slotIds) => {
    setBusy(slotIds ? `roll-${slotIds[0]}` : "roll");
    setNotes([]);
    try {
      const r = await api.post(`/silos/${siloId}/voice-kit/reroll`, { slot_ids: slotIds || null });
      setState((s) => ({ ...s, kit: r.data.kit, has_history: true }));
      setNotes(r.data.notes || []);
      setDraft({});
      load();
      toast[r.data.filled ? "success" : "error"](
        r.data.filled ? `Pulled ${r.data.filled} fresh sample${r.data.filled === 1 ? "" : "s"}.` : "Nothing could be pulled — see the notes below.",
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-roll failed.");
    } finally { setBusy(""); }
  };

  const revert = async () => {
    setBusy("revert");
    try {
      const r = await api.post(`/silos/${siloId}/voice-kit/revert`, {});
      setState((s) => ({ ...s, kit: r.data.kit, has_history: !!(r.data.kit.history || []).length }));
      setDraft({});
      load();
      toast.success("Reverted to the previous set.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nothing to revert to.");
    } finally { setBusy(""); }
  };

  // Oct 1, 2026 (Randy — "after running the tool the voice kit shows up
  // blank"). Both answers used to be read straight off `.answer`, so when the
  // model failed on ONE arm the panel painted empty with no explanation, which
  // reads as "the Voice Kit is broken". `/probe` deliberately returns HTTP 200
  // with `answer: ""` + `llm_error` on an LLM failure so retrieval tuning still
  // works during an outage. We now keep that error, retry a transient fault
  // once, and say what happened on the side that failed.
  const askSide = async (q, mode) => {
    const r = await api.post(`/silos/${siloId}/probe`, { question: q, voice_mode: mode });
    let answer = r.data?.answer || "";
    let error = r.data?.llm_error || "";
    if (!answer && TRANSIENT_LLM.test(error)) {
      await new Promise((res) => setTimeout(res, 1500));
      const again = await api.post(`/silos/${siloId}/probe`, { question: q, voice_mode: mode });
      answer = again.data?.answer || "";
      error = again.data?.llm_error || error;
    }
    return { answer, error };
  };

  const runProbe = async () => {
    const q = probeQ.trim();
    if (!q) return toast.error("Type a question to compare.");
    setBusy("probe");
    setProbe(null);
    try {
      const [kit, legacy] = await Promise.all([askSide(q, "kit"), askSide(q, "legacy")]);
      setProbe({
        kit: kit.answer, legacy: legacy.answer,
        kitError: kit.error, legacyError: legacy.error,
      });
      if ((!kit.answer || !legacy.answer)) toast.error("One side came back empty. The reason is printed in that panel.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Probe failed.");
    } finally { setBusy(""); }
  };

  const loadDrift = async () => {
    setBusy("drift");
    try {
      const r = await api.get(`/silos/${siloId}/voice-drift`);
      setDrift(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not measure the answers.");
    } finally { setBusy(""); }
  };

  if (!state) {
    return <div className="p-6 text-sm text-[#6B6A68]" data-testid="voice-kit-loading">Loading Tone Architect…</div>;
  }
  const { kit, limits, pool, fingerprint, rhythm_directive, overlaps, anti_ai_enabled } = state;
  const activeCount = (kit.slots || []).filter((s) => s.enabled && (s.text || "").trim()).length;
  const activeChars = (kit.slots || [])
    .filter((s) => s.enabled)
    .reduce((n, s) => n + (s.text || "").length, 0);
  const filledCount = (kit.slots || []).filter((s) => (s.text || "").trim()).length;
  const minKept = limits.min_kept || 3;
  const atDeleteFloor = filledCount <= minKept;
  const emptySlots = (kit.slots || []).length - filledCount;

  return (
    <div className="space-y-6" data-testid="voice-kit">
      <div>
        <h2 className="font-serif-display text-3xl text-[#1C1B1A]">Tone Architect</h2>
        <p className="text-sm text-[#6B6A68] mt-2 max-w-3xl leading-relaxed">
          A few short writing samples of your own, so {siloName || "your silo"} answers in your
          voice. Pull them from files you marked <strong className="text-[#2F6B3A]">green</strong> in
          the Library, from replies you flagged as your own words, or type them in yourself.
        </p>
      </div>

      {/* Mode */}
      <div className="bg-white border hairline rounded-lg p-4">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-2">Where the voice comes from</div>
        <div className="flex flex-wrap gap-2">
          {MODES.map((m) => (
            <button
              key={m.v}
              onClick={() => patch({ mode: m.v })}
              data-testid={`voice-kit-mode-${m.v}`}
              title={m.d}
              className={`text-[12px] px-3 py-1.5 rounded-full border transition-colors ${
                kit.mode === m.v ? "bg-[#2F6B3A] text-white border-[#2F6B3A]" : "bg-white text-[#4A5D4E] hairline hover:bg-[#F0F1EE]"
              }`}
            >
              {m.l}
            </button>
          ))}
        </div>
        <p className="text-[11.5px] text-[#6B6A68] mt-2">{MODES.find((m) => m.v === kit.mode)?.d}</p>
        {kit.mode === "kit" && activeCount === 0 && (
          <p className="text-[11.5px] text-[#C16E5A] mt-1" data-testid="voice-kit-empty-warning">
            No samples are on yet, so this behaves like “None”. Add some below.
          </p>
        )}
        {/* Oct 2026 (Randy) — one owner of truth, mirrored not duplicated. The
            explanation used to live in a hover tooltip, which does not exist on
            a phone, and the feature had two different names in two places. */}
        <div className="mt-3 pt-3 border-t hairline" data-testid="voice-kit-anti-ai-mirror">
          <p className="text-[11.5px] text-[#6B6A68] leading-relaxed">
            <strong className="text-[#1C1B1A]">Plain Human Voice</strong>:{" "}
            <strong className={anti_ai_enabled ? "text-[#2F6B3A]" : "text-[#C16E5A]"}>
              {anti_ai_enabled ? "ON" : "OFF"}
            </strong>{" "}
            for this silo. It stops answers reading like a machine wrote them. Works in all three
            modes above, and it is free.{" "}
            <button
              onClick={() => setShowAntiAi((v) => !v)}
              data-testid="voice-kit-anti-ai-more"
              className="text-[#2F6B3A] hover:underline"
            >
              {showAntiAi ? "Hide details" : "What does it do?"}
            </button>
          </p>
          {showAntiAi && (
            <div
              className="mt-2 text-[11.5px] leading-relaxed text-[#4A5D4E] bg-[#F4F6F2] border border-[#DDE4D8] rounded-md px-3 py-2.5 space-y-1.5"
              data-testid="voice-kit-anti-ai-detail"
            >
              <p>Three things give machine writing away. This removes all three.</p>
              <ul className="space-y-1">
                <li>
                  · <strong>Long dashes.</strong> One looks like this: “the body is the expression —
                  the outside of infinite”. Every answer has them taken out after it is written, so
                  this one is guaranteed rather than just asked for.
                </li>
                <li>
                  · <strong>Sentences that are all the same length.</strong> This is the loudest
                  giveaway of the lot. Your silo is told to vary them instead.
                </li>
                <li>
                  · <strong>Stock machine phrasing.</strong> “Great question”, “not just X but Y”,
                  repeating your question back at you, and a summary paragraph at the end that adds
                  nothing.
                </li>
              </ul>
              <p className="text-[#6B6A68]">
                No extra AI call, so it adds nothing to what an answer costs you.
              </p>
            </div>
          )}
          <button
            onClick={() => onSwitchTab?.("settings")}
            data-testid="voice-kit-anti-ai-goto-settings"
            className="mt-1.5 text-[11px] text-[#2F6B3A] hover:underline"
          >
            Change it in Settings → Answer Quality
          </button>
        </div>
      </div>

      {/* Size + pool + actions */}
      <div className="bg-white border hairline rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">New sample size</span>
          <input
            type="range"
            min={limits.min_chars}
            max={limits.max_chars}
            step={50}
            value={kit.slot_chars}
            onChange={(e) => setState((s) => ({ ...s, kit: { ...s.kit, slot_chars: Number(e.target.value) } }))}
            onMouseUp={(e) => patch({ slot_chars: Number(e.target.value) }, true)}
            onTouchEnd={(e) => patch({ slot_chars: Number(e.target.value) }, true)}
            data-testid="voice-kit-size-slider"
            className="w-56 accent-[#2F6B3A]"
          />
          <span className="text-[12px] font-mono text-[#1C1B1A]" data-testid="voice-kit-size-value">{kit.slot_chars} chars</span>
          <span className="text-[11px] text-[#6B6A68]" data-testid="voice-kit-active-chars">
            · {activeCount} in use, {activeChars.toLocaleString()} characters sent per answer
          </span>
        </div>
        {/* Oct 2026 (Randy: "the slider is still set to 600?") — the slider only
            ever governed what the puller aims for. It never trimmed saved text,
            and nothing on screen said so. */}
        <p className="text-[10.5px] text-[#6B6A68] -mt-1" data-testid="voice-kit-size-help">
          This only sets how much text “Pull fresh samples” grabs next time. It never shortens
          writing samples you already have.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => reroll(null)}
            disabled={!!busy}
            data-testid="voice-kit-reroll-all"
            title="Fills your empty slots with fresh samples from your green files or flagged replies, and switches each filled slot on. Green-file samples get one quick tidy-up pass (a fraction of a cent); flagged replies cost nothing. Your previous set is kept, so Revert always works."
            className="text-[11.5px] px-3 py-1.5 rounded-md bg-[#2F6B3A] text-white hover:bg-[#3A4A3E] disabled:opacity-40 flex items-center gap-1"
          >
            <ArrowsClockwise size={12} weight="bold" /> {busy === "roll" ? "Pulling…" : "Pull fresh samples"}
          </button>
          <button
            onClick={revert}
            disabled={!!busy || !(kit.history || []).length}
            data-testid="voice-kit-revert"
            className="text-[11.5px] px-3 py-1.5 rounded-md border hairline text-[#4A5D4E] hover:bg-[#F0F1EE] disabled:opacity-40 flex items-center gap-1"
          >
            <ArrowUUpLeft size={12} weight="duotone" /> Revert to previous set
          </button>
          <span className="text-[11px] text-[#6B6A68]" data-testid="voice-kit-pool">
            pool: {pool.green_docs} green doc{pool.green_docs === 1 ? "" : "s"} ({pool.green_chars.toLocaleString()} chars) · {pool.flagged_replies} flagged repl{pool.flagged_replies === 1 ? "y" : "ies"}
          </span>
        </div>
        {pool.green_docs === 0 && pool.flagged_replies === 0 && (
          <p className="text-[11.5px] text-[#8A6A3A]" data-testid="voice-kit-no-pool">
            There is nothing to pull from automatically yet, so “Pull fresh samples” has no source.
            Mark a file green in the Library (tap its icon twice), or flag a reply as your own words
            in YTRE or the Replies inbox. Samples you type in yourself work fine without either.
          </p>
        )}
        {notes.length > 0 && (
          <ul className="text-[11.5px] text-[#8A6A3A] space-y-0.5" data-testid="voice-kit-notes">
            {notes.map((n, i) => <li key={i}>· {n}</li>)}
          </ul>
        )}
        {(overlaps || []).length > 0 && (
          <p className="text-[11.5px] text-[#8A6A3A]" data-testid="voice-kit-overlap-warning">
            {overlaps.map((o) => `Samples ${o.a.replace("slot-", "#")} and ${o.b.replace("slot-", "#")} are ${Math.round(o.containment * 100)}% the same text`).join(". ")}.
            Replace one of them so the Kit has something different to learn from.
          </p>
        )}
        {/* Oct 1, 2026 (Randy) — "this entire section needs to be hidden in a
            chevron, closed by default, and re-worded in plain English." */}
        {fingerprint && (
          <div className="mt-1" data-testid="voice-kit-fingerprint">
            <button
              onClick={() => setShowFp((v) => !v)}
              data-testid="voice-kit-fingerprint-toggle"
              aria-expanded={showFp}
              className="w-full flex items-center gap-1.5 text-[11.5px] text-[#4A5D4E] hover:text-[#1C1B1A]"
            >
              <CaretRight size={11} weight="bold" className={`transition-transform ${showFp ? "rotate-90" : ""}`} />
              <Ruler size={12} weight="duotone" />
              What your writing looks like, measured
            </button>
            {/* Oct 2026 (Randy) — the chevron stays closed, but the numbers are
                the only evidence this feature has that it is doing anything, so
                one line of them stays visible. */}
            <p className="text-[11px] text-[#6B6A68] ml-[26px] mt-0.5" data-testid="voice-kit-fingerprint-summary">
              {fingerprint.mean_words}-word sentences
              {fingerprint.short_rate > 0 ? ` · 1 in ${Math.round(1 / fingerprint.short_rate)} is short` : ""}
              {fingerprint.em_dashes === 0 ? " · no long dashes" : ""}
              {fingerprint.semicolons === 0 ? " · no semicolons" : ""}
              {" "}· from {fingerprint.sentences} sentences of your own writing
            </p>
            {showFp && (
              <div className="mt-2 text-[11.5px] leading-relaxed text-[#4A5D4E] bg-[#F4F6F2] border border-[#DDE4D8] rounded-md px-3 py-2.5 space-y-2" data-testid="voice-kit-fingerprint-body">
                <p>
                  Your samples were measured. Nothing here was written by an AI, and measuring costs
                  nothing. These numbers are handed to your silo as plain instructions, so it copies
                  how you actually write instead of guessing.
                </p>
                <ul className="space-y-1">
                  <li>· Your sentences average <strong>{fingerprint.mean_words} words</strong>, ranging from {fingerprint.min_words} to {fingerprint.max_words}. Your silo is told to vary its sentence length the same way.</li>
                  <li>· <strong>{Math.round(fingerprint.short_rate * 100)}%</strong> of your sentences are five words or shorter. Short, punchy lines are part of your voice, so your silo is told to keep them.</li>
                  <li>· You use <strong>{fingerprint.em_dashes === 0 ? "no" : String(fingerprint.em_dashes)}</strong> long dashes{fingerprint.em_dashes === 0 ? " at all" : ""}. A long dash looks like this: “the body is the expression — the outside of infinite”. It is the most common giveaway that a machine wrote something, and your answers have it stripped out automatically.</li>
                  <li>· You use <strong>{fingerprint.semicolons === 0 ? "no" : String(fingerprint.semicolons)}</strong> semicolons{fingerprint.semicolons === 0 ? ", so your answers won't either" : ""}. A semicolon looks like this: “he is here; she is there”.</li>
                  <li>· Measured across <strong>{fingerprint.sentences} sentences</strong> of your own writing. More samples, or longer ones, make this sharper.</li>
                </ul>
                {rhythm_directive && (
                  <details className="pt-1">
                    <summary className="cursor-pointer text-[11px] text-[#6B6A68] hover:text-[#1C1B1A]">
                      Show the exact wording sent to your silo
                    </summary>
                    <p className="mt-1.5 italic text-[#6B6A68]" data-testid="voice-kit-rhythm-directive">“{rhythm_directive}”</p>
                  </details>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Oct 2026 (Randy: "what if someone deletes 1, 2, 5") — with the Kit
          selected and nothing switched on, the backend silently falls back to
          classic Rule 1. Silent degradation is what cost him a week. */}
      {kit.mode === "kit" && activeCount === 0 && (
        <div className="bg-[#FDF3EE] border border-[#E8C4B0] rounded-lg p-3.5" data-testid="voice-kit-empty-warning">
          <p className="text-[12px] text-[#8A3A1C] leading-relaxed">
            <strong>No writing samples are switched on.</strong> Voice Kit is selected above, but
            with nothing to copy your silo is answering exactly as it would on Legacy. Switch one on,
            or type one in below.
          </p>
        </div>
      )}

      {/* Slots */}
      <div className="space-y-3">
        {(kit.slots || []).map((s, i) => {
          const localText = draft[s.id] !== undefined ? draft[s.id] : (s.text || "");
          const dirty = draft[s.id] !== undefined && draft[s.id] !== (s.text || "");
          // Show three by default; a slot with anything in it always shows.
          const baseVisible = limits.default_live_slots || 3;
          if (i >= baseVisible + extraSlots && !(s.text || "").trim()) return null;
          return (
            <div key={s.id} className={`bg-white border hairline rounded-lg p-4 ${s.enabled ? "" : "opacity-60"}`} data-testid={`voice-kit-slot-${i + 1}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => patch({ slots: [{ id: s.id, enabled: !s.enabled }] }, true)}
                  data-testid={`voice-kit-slot-toggle-${i + 1}`}
                  title={s.enabled
                    ? "In use. This writing sample is sent with every answer."
                    : "Not in use. The text is kept, but it is not sent."}
                  className={`text-[11px] px-2.5 py-1 rounded-full border ${s.enabled ? "bg-[#2F6B3A] text-white border-[#2F6B3A]" : "bg-white text-[#6B6A68] hairline"}`}
                >
                  {s.enabled ? "ON" : "OFF"}
                </button>
                <span className="text-[12px] font-medium text-[#1C1B1A]">Writing sample {i + 1}</span>
                {!s.enabled && !!(s.text || "").trim() && (
                  <span className="text-[10.5px] text-[#8A6A3A]" data-testid={`voice-kit-slot-${i + 1}-off-note`}>
                    not being used · text is kept, tap ON to use it again
                  </span>
                )}
                <div className="flex items-center gap-1">
                  {SOURCES.map((src) => {
                    const Icon = src.icon;
                    return (
                      <button
                        key={src.v}
                        onClick={() => patch({ slots: [{ id: s.id, source: src.v }] }, true)}
                        title={src.l}
                        data-testid={`voice-kit-slot-${i + 1}-source-${src.v}`}
                        className={`text-[10.5px] px-2 py-1 rounded-md border flex items-center gap-1 ${
                          s.source === src.v ? "bg-[#EAF3EC] text-[#2F6B3A] border-[#2F6B3A]" : "bg-white text-[#6B6A68] hairline hover:bg-[#F0F1EE]"
                        }`}
                      >
                        <Icon size={11} weight="duotone" /> {src.l}
                      </button>
                    );
                  })}
                </div>
                <div className="ml-auto flex items-center gap-1">
                  <button
                    onClick={() => patch({ slots: [{ id: s.id, pinned: !s.pinned }] }, true)}
                    title={s.pinned ? "Pinned — a re-roll will skip this slot" : "Pin so a re-roll never replaces it"}
                    data-testid={`voice-kit-slot-${i + 1}-pin`}
                    className={`p-1.5 rounded-md border hairline ${s.pinned ? "bg-[#FCF6E6] text-[#8A6A3A]" : "text-[#6B6A68] hover:bg-[#F0F1EE]"}`}
                  >
                    {s.pinned ? <PushPin size={12} weight="fill" /> : <PushPinSlash size={12} weight="duotone" />}
                  </button>
                  {s.source !== "manual" && (
                    <button
                      onClick={() => reroll([s.id])}
                      disabled={!!busy}
                      data-testid={`voice-kit-slot-${i + 1}-reroll`}
                      className="text-[10.5px] px-2 py-1 rounded-md border hairline text-[#4A5D4E] hover:bg-[#F0F1EE] disabled:opacity-40 flex items-center gap-1"
                    >
                      <ArrowsClockwise size={11} weight="bold" /> {busy === `roll-${s.id}` ? "…" : "Re-roll"}
                    </button>
                  )}
                  {!!(s.text || "").trim() && (
                    <button
                      onClick={() => {
                        if (atDeleteFloor) {
                          window.alert(
                            `You need to keep at least ${minKept} writing samples. Your silo ` +
                            `measures your rhythm from these, and fewer than ${minKept} is not enough ` +
                            `to measure. To swap this one out, type over it or pull a fresh sample ` +
                            `instead of emptying it.`
                          );
                          return;
                        }
                        if (!window.confirm("Empty this writing sample? The text is removed and the slot moves to the bottom of the stack, ready to fill again.")) return;
                        setDraft((d) => { const n = { ...d }; delete n[s.id]; return n; });
                        patch({ slots: [{ id: s.id, clear: true }] });
                      }}
                      disabled={!!busy}
                      title={atDeleteFloor
                        ? `You need to keep at least ${minKept} writing samples. Type over this one instead.`
                        : "Empty this writing sample"}
                      data-testid={`voice-kit-slot-${i + 1}-delete`}
                      className={`p-1.5 rounded-md border hairline disabled:opacity-40 ${atDeleteFloor ? "text-[#B6B4B1] cursor-not-allowed" : "text-[#8A3A1C] hover:bg-[#FDE3DB]"}`}
                    >
                      <Trash size={12} weight="duotone" />
                    </button>
                  )}
                </div>
              </div>

              {s.kind === "qa" && (
                <input
                  value={s.question || ""}
                  onChange={(e) => setState((st) => ({
                    ...st,
                    kit: { ...st.kit, slots: st.kit.slots.map((x) => (x.id === s.id ? { ...x, question: e.target.value } : x)) },
                  }))}
                  onBlur={(e) => patch({ slots: [{ id: s.id, question: e.target.value }] }, true)}
                  placeholder="What was asked"
                  data-testid={`voice-kit-slot-${i + 1}-question`}
                  className="mt-2 w-full text-[12px] border hairline rounded-md px-2 py-1.5 bg-[#FDFCF8]"
                />
              )}

              <textarea
                value={localText}
                onChange={(e) => setDraft((d) => ({ ...d, [s.id]: e.target.value }))}
                placeholder={s.source === "manual"
                  ? "Paste or type a short piece of your own writing — a real answer works best."
                  : "Empty. Hit Re-roll to pull a sample, or switch this slot to “Typed by me”."}
                rows={4}
                data-testid={`voice-kit-slot-${i + 1}-text`}
                className="mt-2 w-full text-[12.5px] leading-relaxed border hairline rounded-md p-2.5 bg-[#FDFCF8] font-serif"
              />
              <div className="flex items-center gap-2 flex-wrap mt-1.5">
                <span className="text-[10.5px] text-[#6B6A68]">
                  {localText.length} chars
                  {s.origin ? ` · from ${s.origin}` : ""}
                  {s.kind === "qa" ? " · real Q&A" : ""}
                </span>
                {!!(s.removed || []).length && (
                  <span className="text-[10.5px] text-[#2F6B3A] flex items-center gap-1" data-testid={`voice-kit-slot-${i + 1}-removed`}>
                    <Prohibit size={11} weight="duotone" /> stripped: {(s.removed || []).join(", ")}
                  </span>
                )}
                {!!s.change_ratio && (
                  <span className="text-[10.5px] text-[#6B6A68] flex items-center gap-1" title="How much the cleaner changed. Anything over 15% is rejected as a rewrite.">
                    <Scales size={11} weight="duotone" /> {Math.round(s.change_ratio * 100)}% cleaned
                  </span>
                )}
                {!!s.raw_text && (
                  <button
                    onClick={() => setShowRaw(showRaw === s.id ? "" : s.id)}
                    data-testid={`voice-kit-slot-${i + 1}-raw`}
                    className="text-[10.5px] text-[#4A5D4E] hover:underline flex items-center gap-1"
                  >
                    <Eye size={11} weight="duotone" /> {showRaw === s.id ? "hide" : "show"} the original
                  </button>
                )}
                {dirty && (
                  <button
                    onClick={() => { patch({ slots: [{ id: s.id, text: localText, source: "manual", enabled: true }] }); setDraft((d) => { const n = { ...d }; delete n[s.id]; return n; }); }}
                    data-testid={`voice-kit-slot-${i + 1}-save`}
                    className="text-[10.5px] px-2 py-1 rounded-md bg-[#2F6B3A] text-white hover:bg-[#3A4A3E]"
                  >
                    Save this writing sample
                  </button>
                )}
              </div>
              {showRaw === s.id && (
                <pre className="mt-2 text-[11.5px] whitespace-pre-wrap text-[#6B6A68] bg-[#F0F1EE] rounded-md p-2.5" data-testid={`voice-kit-slot-${i + 1}-raw-text`}>{s.raw_text}</pre>
              )}
            </div>
          );
        })}
        {(() => {
          const shown = (kit.slots || []).filter(
            (s, i) => i < (limits.default_live_slots || 3) + extraSlots || (s.text || "").trim()
          ).length;
          if (shown >= (kit.slots || []).length) return null;
          return (
            <button
              onClick={() => setExtraSlots((n) => n + 1)}
              data-testid="voice-kit-add-slot"
              title="There are always five positions. Emptying one never removes it."
              className="text-[11.5px] px-3 py-1.5 rounded-md border hairline text-[#4A5D4E] hover:bg-[#F0F1EE] flex items-center gap-1"
            >
              <Plus size={12} weight="bold" /> Add another writing sample
              <span className="text-[#6B6A68]">({emptySlots} free)</span>
            </button>
          );
        })()}
      </div>

      {/* Ban list */}
      <div className="bg-white border hairline rounded-lg p-4">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-2 flex items-center gap-1">
          <Prohibit size={12} weight="duotone" /> Never say
        </div>
        {(kit.ban_list || []).length === 0 ? (
          <p className="text-[11.5px] text-[#6B6A68]">
            Empty. This fills itself with the filler words tidied out of your samples.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5" data-testid="voice-kit-ban-list">
            {(kit.ban_list || []).map((b) => (
              <span key={b} className="text-[11px] px-2 py-0.5 rounded-full bg-[#FDE3DB] text-[#8A3A1C] flex items-center gap-1">
                “{b}”
                <button
                  onClick={() => patch({ ban_list: (kit.ban_list || []).filter((x) => x !== b) }, true)}
                  data-testid={`voice-kit-ban-remove-${b}`}
                  className="hover:text-[#5A2010]"
                ><X size={9} weight="bold" /></button>
              </span>
            ))}
          </div>
        )}
        <p className="text-[10.5px] text-[#6B6A68] mt-2">
          Single words are banned only as filler or openers, so ordinary sentences still work.
          Multi-word phrases are banned outright.
        </p>
      </div>

      {/* A/B */}
      <div className="bg-white border hairline rounded-lg p-4">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-2 flex items-center gap-1">
          <Waveform size={12} weight="duotone" /> Hear the difference before you commit
        </div>
        {activeCount === 0 && (
          <p className="text-[11.5px] text-[#8A6A3A] mb-2" data-testid="voice-kit-probe-empty-warning">
            No samples are on yet, so both sides run the <strong>identical</strong> prompt — this
            comparison can only show you the model's own variation between two asks, not the Kit.
            Fill a slot first and the left side changes for real.
          </p>
        )}
        {activeCount > 0 && kit.mode !== "kit" && (
          <p className="text-[11.5px] text-[#8A6A3A] mb-2" data-testid="voice-kit-probe-mode-warning">
            This test forces one side through the Kit, so the comparison below is real — but your
            live setting is <strong>{(MODES.find((m) => m.v === kit.mode) || {}).l || kit.mode}</strong>,
            which means real answers (including YTRE replies) keep using {(MODES.find((m) => m.v === kit.mode) || {}).l || kit.mode} until
            you switch the mode at the top.
          </p>
        )}
        <div className="flex gap-2">
          <input
            value={probeQ}
            onChange={(e) => setProbeQ(e.target.value)}
            placeholder="Ask your silo something a fan would ask…"
            data-testid="voice-kit-probe-input"
            className="flex-1 text-[12.5px] border hairline rounded-md px-2.5 py-1.5 bg-[#FDFCF8]"
          />
          <button
            onClick={runProbe}
            disabled={!!busy}
            data-testid="voice-kit-probe-run"
            className="text-[11.5px] px-3 py-1.5 rounded-md border hairline text-[#4A5D4E] hover:bg-[#F0F1EE] disabled:opacity-40"
          >
            {busy === "probe" ? "Asking…" : "Compare"}
          </button>
        </div>
        {probe && (
          <div className="grid md:grid-cols-2 gap-3 mt-3">
            <ProbeSide
              label="With the Voice Kit"
              labelClass="text-[#2F6B3A]"
              text={probe.kit}
              error={probe.kitError}
              testid="voice-kit-probe-kit"
            />
            <ProbeSide
              label="Legacy (Tier 1 as the filter)"
              labelClass="text-[#6B6A68]"
              text={probe.legacy}
              error={probe.legacyError}
              testid="voice-kit-probe-legacy"
            />
          </div>
        )}
        <p className="text-[10.5px] text-[#6B6A68] mt-2">
          Deliberately ask about something your samples are NOT about. That is the test for whether
          a sample's subject matter leaks into an answer.
        </p>
      </div>

      {/* Voice drift — Oct 1, 2026 (Randy pt-voice-drift). The honest version of
          the comparison report he withdrew: not a diff of two outputs, but a
          check of real answers against his own measured writing. Reads answers
          already stored in chat_sessions, no new storage, no LLM, no per-ask
          write. Loads on demand so opening this tab costs nothing. */}
      <div className="bg-white border hairline rounded-lg p-4">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-2 flex items-center gap-1">
          <Ruler size={12} weight="duotone" /> Is it still sounding like you?
        </div>
        <p className="text-[11.5px] text-[#6B6A68] mb-2">
          Measures the answers your silo has actually given and compares them with your own writing.
          Free, and no AI is involved.
        </p>
        <button
          onClick={loadDrift}
          disabled={busy === "drift"}
          data-testid="voice-kit-drift-run"
          className="text-[11.5px] px-3 py-1.5 rounded-md border hairline text-[#4A5D4E] hover:bg-[#F0F1EE] disabled:opacity-40"
        >
          {busy === "drift" ? "Measuring…" : drift ? "Check again" : "Check the last 20 answers"}
        </button>
        {drift && (
          <div className="mt-3" data-testid="voice-kit-drift-result">
            {!drift.checks ? (
              <p className="text-[11.5px] text-[#8A6A3A]" data-testid="voice-kit-drift-empty">{drift.message}</p>
            ) : (
              <>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className={`text-2xl font-serif-display ${drift.score >= 70 ? "text-[#2F6B3A]" : drift.score >= 40 ? "text-[#8A6A3A]" : "text-[#C16E5A]"}`} data-testid="voice-kit-drift-score">
                    {drift.score}%
                  </span>
                  <span className="text-[11.5px] text-[#6B6A68]">
                    match across {drift.answers_measured} answer{drift.answers_measured === 1 ? "" : "s"}
                    {drift.measured_mode ? (
                      <> written with <strong className="text-[#1C1B1A]">{MODE_LABEL[drift.measured_mode] || drift.measured_mode}</strong></>
                    ) : null}
                  </span>
                </div>
                {(drift.by_mode || []).length > 1 && (
                  <p className="text-[11px] text-[#6B6A68] mt-1" data-testid="voice-kit-drift-by-mode">
                    {drift.by_mode
                      .map((b) => `${MODE_LABEL[b.mode] || b.mode}: ${b.score}% over ${b.answers} answer${b.answers === 1 ? "" : "s"}`)
                      .join(" · ")}
                  </p>
                )}
                {drift.measured_mode && drift.live_mode && drift.measured_mode !== drift.live_mode && (
                  <p className="text-[11px] text-[#8A6A3A] mt-1" data-testid="voice-kit-drift-stale">
                    None of these answers were written with your current setting, so they say nothing
                    about how {MODE_LABEL[drift.live_mode] || drift.live_mode} is doing. Answer a few
                    questions, then check again.
                  </p>
                )}
                <ul className="mt-2 space-y-1 text-[11.5px] leading-relaxed">
                  {drift.checks.map((c, i) => (
                    <li key={i} className={c.ok ? "text-[#4A5D4E]" : "text-[#8A6A3A]"} data-testid={`voice-kit-drift-check-${i}`}>
                      {c.ok ? "✓" : "·"} {c.note}
                    </li>
                  ))}
                </ul>
                {/* Oct 2026 (Randy pt-voice-repetition) — the repeated phrase,
                    named. The generic checks list already shows whether it
                    passed; this prints the actual words so the finding is
                    actionable instead of just a failed row. */}
                {(drift.repetition?.worst_count || 0) > 1 && (
                  <p className="text-[11px] text-[#8A6A3A] mt-1.5" data-testid="voice-kit-drift-repetition">
                    Most repeated phrase: “{drift.repetition.worst_phrase}” — {drift.repetition.worst_count} times
                    inside one answer.
                  </p>
                )}
                {!!drift.skipped_short && (
                  <p className="text-[10.5px] text-[#6B6A68] mt-1.5" data-testid="voice-kit-drift-skipped">
                    {drift.skipped_short} very short answer{drift.skipped_short === 1 ? "" : "s"} skipped.
                    One-liners and test questions would drag these numbers around.
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
